import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy
} from '@angular/router';

/**
 * A route strategy allowing for explicit route reuse.
 * Used as a workaround for https://github.com/angular/angular/issues/18374
 * To reuse a given route, add `data: { reuse: true }` to the route definition.
 */
export class RouteReusableStrategy extends RouteReuseStrategy {
  private storedRoutes = new Map<string, DetachedRouteHandle>();

  private getRouteKey(route: ActivatedRouteSnapshot): string | null {
    const routerState = (route as any)?._routerState;
    return routerState?.url || null;
  }

  private isReusableRoute(route: ActivatedRouteSnapshot): boolean {
    return route.data?.reuse === true;
  }

  public clearStoredRoutes(): void {
    this.storedRoutes.clear();
  }

  public shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.isReusableRoute(route);
  }

  public store(
    route: ActivatedRouteSnapshot,
    detachedTree: DetachedRouteHandle | null
  ): void {
    const key = this.getRouteKey(route);
    if (!key) return;
    if (detachedTree && this.isReusableRoute(route)) {
      this.storedRoutes.set(key, detachedTree);
    }
  }

  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getRouteKey(route);
    return !!(key && this.storedRoutes.has(key));
  }

  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    return key ? this.storedRoutes.get(key) || null : null;
  }

  public shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
